import { useEffect, useState } from 'react';
import FlexService from '../../services/FlexService';

/**
 * Data-driven visibility from a component's JSON config -- no entity metadata or
 * schema involved. A visibility script is a FLEX command whose result decides
 * whether something shows: anything but an explicit false (including an error)
 * means visible, so a broken check never hides a field or column.
 *
 * Forms -- rapidEntry headerPhase / headerSummary / lineEntry "fields" accept either a
 * plain field name or an object with an optional visibleScript (the same {"name": ...}
 * shape resolverPhase fields already use):
 *
 *   "fields": ["receiptDate", {"name": "ownerId", "visibleScript": "has_owners"}]
 *
 * Grids -- a grid block may map column field names to a visibility script:
 *
 *   "columnVisibility": {"ownerId": "has_owners"}
 */

// Each script's result is cached briefly so a page with several forms/grids runs it
// once, while e.g. a newly created Owner still shows up within a minute.
const CACHE_MS = 60 * 1000;
const scriptCache = {};

export const evalVisibleScript = (scriptName) => {
  const hit = scriptCache[scriptName];
  if (hit && Date.now() - hit.at < CACHE_MS) return hit.promise;
  const promise = FlexService.executeScript({ scriptName, context: {} })
    .then((resp) => resp?.context?.result !== false)
    .catch(() => true);
  scriptCache[scriptName] = { promise, at: Date.now() };
  return promise;
};

export const fieldEntryName = (entry) => (entry && typeof entry === 'object' ? entry.name : entry);

/** Resolves a form's config field list to the names that should be shown, in order. */
export const resolveVisibleFieldNames = async (entries = []) => {
  const scripts = [...new Set(entries
    .map((e) => (e && typeof e === 'object' ? e.visibleScript : null))
    .filter(Boolean))];
  const visibleByScript = {};
  await Promise.all(scripts.map(async (s) => { visibleByScript[s] = await evalVisibleScript(s); }));
  return entries
    .filter((e) => !(e && typeof e === 'object' && e.visibleScript) || visibleByScript[e.visibleScript])
    .map(fieldEntryName)
    .filter(Boolean);
};

/** Every visibility script named by a columnVisibility map anywhere inside a view/workspace definition. */
const collectColumnVisibilityScripts = (node, found = new Set()) => {
  if (Array.isArray(node)) {
    node.forEach((n) => collectColumnVisibilityScripts(n, found));
  } else if (node && typeof node === 'object') {
    if (node.columnVisibility && typeof node.columnVisibility === 'object') {
      Object.values(node.columnVisibility).forEach((s) => s && found.add(s));
    }
    Object.values(node).forEach((v) => collectColumnVisibilityScripts(v, found));
  }
  return found;
};

/**
 * Hook: evaluates every columnVisibility script referenced in `definition` and returns
 * {scriptName: boolean}. Until a script resolves its columns stay visible.
 */
export const useColumnVisibility = (definition) => {
  const [visibleByScript, setVisibleByScript] = useState({});
  useEffect(() => {
    let cancelled = false;
    const scripts = [...collectColumnVisibilityScripts(definition)];
    if (scripts.length === 0) return undefined;
    Promise.all(scripts.map(async (s) => [s, await evalVisibleScript(s)])).then((pairs) => {
      if (!cancelled) setVisibleByScript(Object.fromEntries(pairs));
    });
    return () => { cancelled = true; };
  }, [definition]);
  return visibleByScript;
};

/** Drops grid columns whose columnVisibility script resolved to false. */
export const applyColumnVisibility = (columns, columnVisibility, visibleByScript) => {
  if (!columnVisibility) return columns;
  return columns.filter((c) => {
    const script = columnVisibility[c.columnId];
    return !script || visibleByScript[script] !== false;
  });
};

/**
 * A field's own condition (field metadata visibleWhenField / visibleWhenValues, FH-316): shown only while that other
 * field of the same form has one of the listed values (comma-separated). A field without a condition is always shown.
 */
export const isFieldShownFor = (field, values) => {
  if (!field || !field.visibleWhenField) return true;
  const allowed = String(field.visibleWhenValues || '').split(',').map((v) => v.trim()).filter(Boolean);
  const current = values ? values[field.visibleWhenField] : undefined;
  return allowed.includes(String(current ?? ''));
};
